
import React, { useEffect, useState, useRef } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
  animate
} from "framer-motion";
import {
  Check,
  Zap,
  Building2,
  Rocket,
  ShieldCheck,
  Plus,
  ChevronRight,
  LucideIcon,
  Sparkles
} from "lucide-react";



interface PricingTierProps {
  name: string;
  price: string;
  description: string;
  features: string[];
  icon: LucideIcon;
  highlight?: boolean;
  index: number;
}

const PricingCard = ({
  name,
  price,
  description,
  features,
  icon: Icon,
  highlight
}: PricingTierProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const x = useMotionValue(0.5);
  const y = useMotionValue(0.5);
  const spotlightX = useMotionValue(0);
  const spotlightY = useMotionValue(0);

  const rotateX = useSpring(
    useTransform(y, [0, 1], ["10deg", "-10deg"]),
    { stiffness: 60, damping: 25 }
  );

  const rotateY = useSpring(
    useTransform(x, [0, 1], ["-10deg", "10deg"]),
    { stiffness: 60, damping: 25 }
  );

  useEffect(() => {
    if (!isHovered) {
      const controlsX = animate(x, [0.4, 0.6, 0.4], {
        duration: 5,
        repeat: Infinity,
        ease: "easeInOut"
      });

      const controlsY = animate(y, [0.4, 0.4, 0.6, 0.4], {
        duration: 7,
        repeat: Infinity,
        ease: "easeInOut"
      });

      const updateSpotlight = () => {
        if (cardRef.current) {
          spotlightX.set(x.get() * cardRef.current.offsetWidth);
          spotlightY.set(y.get() * cardRef.current.offsetHeight);
        }
      };

      const unsubX = x.on("change", updateSpotlight);
      const unsubY = y.on("change", updateSpotlight);

      return () => {
        controlsX.stop();
        controlsY.stop();
        unsubX();
        unsubY();
      };
    }
  }, [isHovered]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width);
    y.set((e.clientY - rect.top) / rect.height);
    spotlightX.set(e.clientX - rect.left);
    spotlightY.set(e.clientY - rect.top);
  };

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setIsHovered(false)}
      style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
      className={`relative p-10 rounded-[2.5rem] flex flex-col h-full transition-all duration-500 overflow-hidden bg-white border ${
        highlight
          ? "border-blue-500/40 shadow-[0_20px_60px_rgba(59,130,246,0.12)]"
          : "border-slate-200 hover:border-slate-300 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
      }`}
    >
      {/* Spotlight */}
      <motion.div
        className="pointer-events-none absolute -inset-px opacity-30 group-hover:opacity-60 transition-opacity duration-500"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              300px circle at ${spotlightX}px ${spotlightY}px,
              ${
                highlight
                  ? "rgba(59,130,246,0.12)"
                  : "rgba(59,130,246,0.06)"
              },
              transparent 80%
            )
          `
        }}
      />

      <div
        style={{ transform: "translateZ(40px)" }}
        className="relative z-10 flex flex-col h-full"
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-10">
          <div
            className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 ${
              highlight
                ? "bg-blue-600 text-white"
                : "bg-slate-100 text-slate-600"
            }`}
          >
            <Icon size={26} strokeWidth={1.2} />
          </div>

          {highlight && (
            <span className="text-[10px] font-mono font-bold tracking-[0.2em] text-blue-600 uppercase px-3 py-1 rounded-full bg-blue-50 border border-blue-200">
              Popular
            </span>
          )}
        </div>
         
        {/* Pricing */}
        <div className="mb-8">
          <h3 className="text-2xl font-bold mb-2 tracking-tight text-slate-900">
            {name}
          </h3>

          <div className="flex items-baseline gap-1 mb-4">
            <span className="text-5xl font-black text-slate-900">
              {price}
            </span>

            {price !== "Free" && price !== "Custom" && (
              <span className="text-slate-500 text-[10px] font-mono uppercase tracking-[0.2em] ml-2">
                / month
              </span>
            )}
          </div>

          <p className="text-slate-600 text-sm leading-relaxed font-light line-clamp-2">
            {description}
          </p>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-10 flex-grow">
          {features.map((feature, i) => (
            <div key={i} className="flex items-start gap-3">
              <div
                className={`mt-1 ${
                  highlight ? "text-blue-600" : "text-slate-400"
                }`}
              >
                <Check size={14} />
              </div>
              <span className="text-slate-600 text-sm font-light leading-snug">
                {feature}
              </span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <button className={`group relative w-full py-4 rounded-2xl font-bold text-xs uppercase tracking-[0.2em] overflow-hidden transition-all active:scale-95 ${
          highlight 
          ? "bg-blue-600 text-white shadow-xl shadow-blue-200" 
          : "bg-slate-900 text-white hover:bg-black"
      }`}>
        <span className="relative z-10">{price === "Custom" ? "Contact Sales" : "Get Started"}</span>
        <motion.div 
          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          animate={{ x: isHovered ? ['-100%', '100%'] : '-100%' }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
      </button>
        
      </div>
    </motion.div>
  );
};

const PricingSection = () => {
  const tiers = [
    {
      name: "Identity",
      price: "Free",
      description: "Core identity layer for entering the Gao Internet.",
      icon: Rocket,
      features: [
        "Domain-native login",
        "Basic profile",
        "Limited file storage",
        "Community support"
      ]
    },
    {
      name: "Starter",
      price: "$12",
      description: "For creators and solo builders starting their workspace.",
      icon: Sparkles,
      features: [
        "Direct chat",
        "Domain mail",
        "Basic documents",
        "1–3 AI agents",
        "Basic API keys"
      ]
    },
    {
      name: "Pro",
      price: "$29",
      description: "For small teams collaborating and automating workflows.",
      highlight: true,
      icon: Zap,
      features: [
        "Team chat",
        "Shared mailboxes",
        "Calendar",
        "Expanded storage",
        "Advanced AI agents",
        "Audit logs (90 days)",
        "Basic payment console"
      ]
    },
    {
      name: "Business",
      price: "$79",
      description: "Designed for revenue-generating organizations requiring integrated operational infrastructure.",
      icon: Building2,
      features: [
        "Full commercial console",
        "Loyalty and incentive modules",
        "Affiliate management",
        "Multi-role governance controls",
        "Advanced analytics",
        "Compliance configuration tools"
      ]
    },
    {
      name: "Enterprise",
      price: "Custom",
      description:
        "Enterprise engagements available upon request.",
      icon: ShieldCheck,
      features: [ 
        "Dedicated infrastructure clusters",
        "Advanced security controls",
        "Regulatory configuration support",
        "Custom integration frameworks", 
      ]
    }
  ];

  return (
    <section
      id="pricing"
      className="relative py-32 bg-[#F8F9FF] text-[#1A1A1A] overflow-hidden"
    >
      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mb-24">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className="inline-block rounded-full px-4 py-1.5 font-mono border border-indigo-100 bg-indigo-50 text-[10px] font-bold tracking-[0.3em] uppercase mb-4 text-blue-600"
          >
            Commercial
          </motion.div>

          <h2 className="text-5xl md:text-7xl tracking-tighter mb-8 leading-tight text-slate-900">
            Strategic Pricing
          </h2>

          <p className="text-slate-600 text-lg md:text-xl font-light max-w-2xl leading-relaxed">
            An identity-driven business model: domains bring users in,
            Workspace keeps them operating. Select the infrastructure aligned
            with your scale.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((tier, index) => (
            <PricingCard key={index} {...tier} index={index} />
          ))}
        </div>

        {/* Add-ons */}
        <motion.div
          whileHover={{ y: -6 }}
          transition={{ type: "spring", stiffness: 200, damping: 18 }}
          className="mt-20 p-12 rounded-[2.5rem] border border-slate-200 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.05)] flex flex-col md:flex-row items-center justify-between gap-10 transition-all duration-500 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)]"
        >
          <div className="flex items-center gap-8">
            <div className="w-18 h-18 rounded-[1.75rem] bg-blue-100 border border-blue-200 flex items-center justify-center text-blue-600 transition-all duration-500">
              <Plus size={30} />
            </div>

            <div>
              <h4 className="text-2xl font-semibold mb-2 tracking-tight text-slate-900">
                Extended Capabilities
              </h4>
              <p className="text-slate-600 font-light text-sm md:text-base leading-relaxed max-w-xl">
                Expand your workspace with additional AI credits, storage
                capacity, and advanced automation modules designed for
                protocol-scale operations.
              </p>
            </div>
          </div>

          <button className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-blue-600 hover:text-blue-700 transition-all duration-300">
            Explore Add-Ons
            <ChevronRight size={16} />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
